/**
 * Empty states say what and why (§12.1). "No remuneration rows — this data
 * comes from board reports, and this corpus has one" beats an illustration
 * of an empty box.
 */
import type { ReactNode } from 'react'
import { cn } from '~/lib/cn.ts'
import { Button } from './primitives.tsx'

export function EmptyState({
  title, children, action, className, compact,
}: {
  title: ReactNode
  children?: ReactNode
  action?: ReactNode
  className?: string
  compact?: boolean
}) {
  return (
    <div
      className={cn(
        'rounded-[var(--radius-container)] border border-dashed border-[var(--border)]',
        'bg-[var(--bg-raised)]/40 text-start',
        compact ? 'px-3 py-3' : 'px-5 py-6',
        className,
      )}
    >
      <div className="text-[length:var(--text-prose)] font-medium text-[var(--text)]">{title}</div>
      {children && (
        <div className="mt-1.5 max-w-[62ch] text-[length:var(--text-data)] leading-[var(--text-data--line-height)] text-[var(--text-muted)]">
          {children}
        </div>
      )}
      {action && <div className="mt-3">{action}</div>}
    </div>
  )
}

export function ErrorState({
  title, detail, requestId, onRetry, retryLabel = 'Try again',
}: {
  title: ReactNode
  detail?: ReactNode
  requestId?: string | null
  onRetry?: () => void
  retryLabel?: string
}) {
  return (
    <div className="rounded-[var(--radius-container)] border border-[var(--fail)]/40 bg-[var(--fail-bg)] px-4 py-3">
      <div className="text-[length:var(--text-prose)] font-medium text-[var(--fail)]">{title}</div>
      {detail && (
        <div className="mt-1 max-w-[70ch] text-[length:var(--text-data)] text-[var(--text-muted)]">
          {detail}
        </div>
      )}
      {requestId && (
        <div className="mt-1.5 font-[family-name:var(--font-mono)] text-[length:var(--text-tiny)] text-[var(--text-faint)]">
          request {requestId}
        </div>
      )}
      {onRetry && (
        <Button size="sm" className="mt-2.5" onClick={onRetry}>
          {retryLabel}
        </Button>
      )}
    </div>
  )
}

/** A shell command the user is meant to run. Selectable, copyable, never wrapped. */
export function Command({ children }: { children: string }) {
  return (
    <code
      dir="ltr"
      className={cn(
        'mt-1.5 block w-fit max-w-full overflow-x-auto rounded-[var(--radius-control)]',
        'border border-[var(--border)] bg-[var(--bg-sunken)] px-2 py-1',
        'font-[family-name:var(--font-mono)] text-[length:var(--text-tiny)] text-[var(--text)]',
      )}
    >
      {children}
    </code>
  )
}
